import { CUSTOM_WATERFALL_RANGES } from "./DatapointTypes";
import type { CustomWaterfallRange } from "./DatapointTypes";
import { PREDEFINED_CHECKPOINTS } from "./PredefinedCheckpoint";
import type { PredefinedCheckpoint } from "./PredefinedCheckpoint";

/**
 * Core column names in the standard analytics schema.
 *
 * This const array is the single source of truth for both the runtime
 * allowlist (used by SchemaEnforcementPlugin) and the TypeScript union type.
 * To add a new core column, add it here — both consumers update automatically.
 */
export const CORE_ANALYTICS_SCHEMA_COLUMNS = [
    "ActionSource",
    "App",
    "AutomationType",
    "BEServer",
    "BundleTime",
    "Cache",
    "ClientId",
    "DataRetries",
    "DataSource",
    "DeviceId",
    "E2ETimeElapsed",
    "EcsETag",
    "ErrorComponent",
    "EventArea",
    "EventName",
    "ActingPuid",
    "ExtraData",
    "Forest",
    "Health",
    "Host",
    "HostedScenario",
    "HostVersion",
    "Layout",
    "MailboxType",
    "MBXGuid",
    "MultiAccountType",
    "OriginalThread",
    "PhysicalRing",
    "Puid",
    "Ring",
    "Scenario",
    "SequenceNumber",
    "Session.Id",
    "SessionElapseTime",
    "SessionOccurences",
    "Start",
    "Status",
    "TenantGuid",
    "Thread",
    "ThroughAFD",
    "Trace",
    "TrueDuration",
    "Type",
    "UserInfo.Id",
    "UserInfo.IdType",
    "UserPerceivedTime",
    "UserType",
    "VariantEnv",
    "cV",
    "diagnostics",
    "message",
    "stack",
    "online",
    // ETag itself rides on the EcsETag common property (declared above),
    // auto-stamped on every Aria event, so no separate ETag column is needed.
    "EcsConfigIds",
    "EcsRolloutIds",
    "EcsConfigAliases",
    "EcsPairedStages",
    // Stamped on the EcsConfigStateStamping event (client_ecs_config_state).
    "BootFlights",
] as const;

/**
 * Network-specific column names in the extended analytics schema.
 *
 * This const array is the single source of truth for both the runtime
 * allowlist (used by SchemaEnforcementPlugin) and the TypeScript union type.
 */
export const EXTENDED_NETWORK_ANALYTICS_SCHEMA_COLUMNS = [
    "AfdId",
    "AttemptCount",
    "FEServer",
    "CalcBEServer",
    "ConnectEnd",
    "ConnectStart",
    "ContentEncoding",
    "DomainLookupEnd",
    "DomainLookupStart",
    "ErrorMessage",
    "ErrorDetails",
    "ErrorCode",
    "ErrorType",
    "FetchStart",
    "MessageServiceAvailability",
    "NextHopProtocol",
    "DomainName",
    "RedirectEnd",
    "RedirectStart",
    "RequestCredentialsOption",
    "RequestIds",
    "RequestStart",
    "ResponseContentLength",
    "ResponseEnd",
    "ResponseStart",
    "SecureConnectionStart",
    "ServiceGatewayOrigin",
    "TrafficSplitterResponseOrigin",
    "WorkerStart",
    "responseRecieved",
    "OwaServiceResultType",
    "ReturnCode",
] as const;

/**
 * All valid waterfall column names (WF_-prefixed).
 *
 * Generated from the PredefinedCheckpoint and CustomWaterfallRange source-of-
 * truth arrays.  Used by SchemaEnforcementPlugin to validate waterfall columns
 * by exact match instead of prefix.
 */
export const WATERFALL_COLUMNS: string[] = [
    ...PREDEFINED_CHECKPOINTS.map(c => `WF_${c}`),
    ...CUSTOM_WATERFALL_RANGES.map(r => `WF_Custom${r}`),
];

/**
 * Column names that are set by the analytics pipeline code at runtime
 * but are not part of the formal AnalyticsSchema type definition.
 *
 * Each entry documents which source file sets the column on item.data.
 * If you add a new property to item.data anywhere in the pipeline,
 * add it here — SchemaEnforcementPlugin will strip it otherwise.
 */
export const RUNTIME_ADDED_COLUMNS = [
    // ── logDatapoint.ts (owa-analytics-core) ─────────────────────────────────
    "EventType",
    "ServiceVersion",
    "Dag",
    "OriginalEvent",
    "Sampled",
    "BatchId",
    "MiscData",
    "PiiData",
    "IsScorecardEvent",
    
    // ── logSessionDatapoint.ts (owa-analytics-core) ──────────────────────────
    "Features",
    "UserAgent",
    "BootType",
    "BootFlights",
    "dag", // lowercase — set by session code, distinct from QoS 'Dag'
    "Hardware",
    "Capabilities",
    "ErrorCodes",
    "ECSFeatures",
    "Settings",
    "Dates",
    "LoadTime",
    "HashEmail",
    
    // ── logPerformanceDatapoint.ts (owa-analytics-core) ──────────────────────
    "Waterfall",
    
    // ── OneDsWrapper.ts (owa-analytics-core) ─────────────────────────────────
    "HostTelemetry",
    
    // ── addErrorToCustomData.ts (owa-analytics-utils) ────────────────────────
    // These columns are set by addErrorToCustomData and passed as direct
    // properties on ErrorDatapoint (via its `props` constructor arg), so they
    // appear as top-level keys in item.data.
    "background",
    "errorMessage",
    "name",
    "file",
    "line",
    "col",
    "httpstatus",
    "responseCode",
    "innerMessage",
    "cause",
    "gqlPath",
    "queryStack",
    
    // ── ignoreError.ts / addFinalErrorFieldsToCustomData (owa-analytics-utils)
    "status",
    "fet",
    
    // ── logErrorDatapoint.ts (owa-analytics) ─────────────────────────────────
    "errorVersion",
] as const;

export type CoreAnalyticsSchema = (typeof CORE_ANALYTICS_SCHEMA_COLUMNS)[number];

type ExtendedNetworkAnalyticsSchema = (typeof EXTENDED_NETWORK_ANALYTICS_SCHEMA_COLUMNS)[number];

export type AnalyticsSchema = CoreAnalyticsSchema | ExtendedNetworkAnalyticsSchema | WaterfallColumns;

export type WaterfallColumns = `WF_${PredefinedCheckpoint}` | `WF_Custom${CustomWaterfallRange}`;
